import { observer } from 'mobx-react'
import { FC, useContext, useState, MouseEvent, useMemo } from 'react'
import './Buttons.css'
import { Button } from '@components//Generic'
import { StoreContext } from '@store/Root.store'
import { LoadingSpinner } from '@components//Generic'
import { DownloadDocumentButton } from '@/components/DownloadDocumentButton/DownloadDocumentButton'
import { getDocumentUrl } from '@/methods/getDocumentUrl'
import { RejectButton, TransRejectProps } from './RejectButton'
import { cx } from '@/utils/cx'

interface ButtonsProps {
  documentId: string
  defaultCheckboxChecked: boolean
  checkboxClauseEnabled: boolean
  clauseCheckboxChecked: boolean
  showButtonHint?: boolean
  showRejectButton?: boolean
  showPreviousButton?: boolean
  handleSign: () => Promise<boolean>
  handlePrevious?: () => void
  setAllowSigning?: (allow: boolean) => void
  isButtonDisabled: boolean
  isDocReady?: boolean
  btnLoading: boolean
  transProceed: string
  transHint: string
  transReject: TransRejectProps
  transPrevious?: string
  service?: string
}

export const Buttons: FC<ButtonsProps> = observer(
  ({
    documentId,
    defaultCheckboxChecked,
    checkboxClauseEnabled,
    clauseCheckboxChecked,
    showButtonHint,
    showRejectButton,
    showPreviousButton,
    handleSign,
    handlePrevious,
    setAllowSigning,
    isButtonDisabled,
    isDocReady,
    btnLoading,
    transProceed,
    transHint,
    transReject,
    transPrevious = '',
    service,
  }) => {
    const store = useContext(StoreContext)
    const { isMobile } = store.AppState

    const [mobileHintVisible, setMobileHintVisible] = useState(false)

    const handleDisabledHint = (e: MouseEvent) => {
      e.preventDefault()

      if (!defaultCheckboxChecked || (checkboxClauseEnabled && !clauseCheckboxChecked)) {
        setMobileHintVisible(true)
        setTimeout(() => setMobileHintVisible(false), 5000)
        return
      }
    }

    const proceedButton = (
      <Button
        onClick={handleSign}
        className="sign-doc-buttons__proceed"
        showCheckboxTooltip={isButtonDisabled && showButtonHint}
        id="page-submit"
        disabled={isButtonDisabled}
      >
        {btnLoading && <LoadingSpinner inButton />}
        <span className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}>
          {transProceed}
        </span>
      </Button>
    )

    const previousButton = useMemo(
      () =>
        handlePrevious && (
          <Button
            onClick={handlePrevious}
            className="button--secondary flex-1 sign-doc-buttons__button button--only-icon-on-mobile"
            aria-label={transPrevious}
            title={transPrevious}
            disabled={!isDocReady}
          >
            <span className="button__icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.0405 17.4592C11.2943 17.7131 11.7059 17.7131 11.9597 17.4592C12.2136 17.2054 12.2136 16.7938 11.9597 16.54L8.06934 12.6496L17.5 12.6496C17.859 12.6496 18.15 12.3586 18.15 11.9996C18.15 11.6406 17.859 11.3496 17.5 11.3496L8.06934 11.3496L11.9597 7.45923C12.2136 7.20539 12.2136 6.79383 11.9597 6.53999C11.7059 6.28615 11.2943 6.28615 11.0405 6.53999L6.04048 11.54C5.78664 11.7938 5.78664 12.2054 6.04048 12.4592L11.0405 17.4592Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            {!isMobile && <span className="generic__button-text">{transPrevious}</span>}
          </Button>
        ),
      [transPrevious, isMobile, handlePrevious]
    )

    return (
      <>
        {mobileHintVisible && showButtonHint && (
          <div className="generic__sign-document-hint-mobile">
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.99992 3.04183C6.15694 3.04183 3.04159 6.15718 3.04159 10.0002C3.04159 13.8431 6.15694 16.9585 9.99992 16.9585C13.8429 16.9585 16.9583 13.8431 16.9583 10.0002C16.9583 6.15718 13.8429 3.04183 9.99992 3.04183ZM1.95825 10.0002C1.95825 5.55887 5.55863 1.9585 9.99992 1.9585C14.4412 1.9585 18.0416 5.55887 18.0416 10.0002C18.0416 14.4415 14.4412 18.0418 9.99992 18.0418C5.55863 18.0418 1.95825 14.4415 1.95825 10.0002Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.99992 14.7085C9.70076 14.7085 9.45825 14.466 9.45825 14.1668L9.45825 9.16683C9.45825 8.86767 9.70076 8.62516 9.99992 8.62516C10.2991 8.62516 10.5416 8.86767 10.5416 9.16683V14.1668C10.5416 14.466 10.2991 14.7085 9.99992 14.7085Z"
                fill="white"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.6249 6.4585C10.6249 6.11332 10.3451 5.8335 9.99992 5.8335C9.65474 5.8335 9.37492 6.11332 9.37492 6.4585C9.37492 6.80367 9.65474 7.0835 9.99992 7.0835C10.3451 7.0835 10.6249 6.80367 10.6249 6.4585Z"
                fill="white"
              />
            </svg>
            <div>{transHint}</div>
          </div>
        )}
        <div className="sign-doc-buttons__wrapper">
          {isMobile && !!showPreviousButton && previousButton}

          {!!documentId && (
            <DownloadDocumentButton
              documentUrl={getDocumentUrl(documentId, service)}
              onClick={() => setAllowSigning && setAllowSigning(true)}
              margin="0"
            />
          )}

          {!!showRejectButton && (
            <RejectButton
              btnDisabled={!documentId}
              transReject={transReject}
              className="button--warning sign-doc-buttons__button flex-1"
            />
          )}

          {isMobile ? (
            <div
              className={cx(
                'generic__sign-document-hint-mobile-button-wrapper',
                !!showRejectButton ? 'w-100' : 'flex-1'
              )}
              onClick={isButtonDisabled && isMobile ? handleDisabledHint : undefined}
            >
              {proceedButton}
            </div>
          ) : (
            <>
              {!!showPreviousButton && previousButton}
              {proceedButton}
            </>
          )}
        </div>
      </>
    )
  }
)
